import React, { useState } from "react";
import {
  Container,
  Typography,
  IconButton,
  Icon,
  Box,
  Button,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Avatar,
} from "@material-ui/core";
import { School, EventNote, People, ExpandMore } from "@material-ui/icons";
import { ModalFullScreen } from "../ModalFullScreen";
import { useStyles } from "./styles";
import { Translator } from "@itarget/shared";
import { useSelector } from "react-redux";

const useTranslation = () => {
  return {
    noResults: Translator({
      transRef: "translation.Nenhum resultado encontrado",
    }).props.children,
    result: Translator({ transRef: "translation.Resultado" }).props.children,
    results: Translator({ transRef: "translation.Resultados" }).props.children,
    performance: Translator({ transRef: "translation.Desempenho" }).props
      .children,
    answeredOn: Translator({ transRef: "translation.Respondido em" }).props
      .children,
    questionnaire: Translator({ transRef: "translation.Questionário" }).props
      .children,
    correctResult: Translator({ transRef: "translation.Correto" }).props
      .children,
    incorrectResult: Translator({ transRef: "translation.Incorreto" }).props
      .children,
    yourAnswer: Translator({ transRef: "translation.Sua Resposta" }).props
      .children,
    correctAnswer: Translator({ transRef: "translation.Resposta Correta" })
      .props.children,
    showAnswer: Translator({ transRef: "translation.Exibir questão" }).props
      .children,
    hideAnswer: Translator({ transRef: "translation.Ocultar questão" }).props
      .children,
    showAnswers: Translator({ transRef: "translation.Exibir respostas" }).props
      .children,
    hideAnswers: Translator({ transRef: "translation.Ocultar respostas" }).props
      .children,
    questionnaireNotAnswered: Translator({
      transRef: "translation.Não respondido",
    }).props.children,
    notAnsweredQuizFV: Translator({
      transRef:
        "translation.Questionário a ser respondido durante a execução do vídeo.",
    }).props.children,
    notAnsweredQuizFA: Translator({
      transRef:
        "translation.Questionário a ser respondido após a reprodução do vídeo.",
    }).props.children,
  };
};

export default function QuizResultsModal({
  results,
  closeShow,
  course,
  isLoading,
  finalResultsByScheduleId,
  isLoadingFinalResult,
}) {
  const user = useSelector((state) => state.auth.user);
  const finalResults =
    finalResultsByScheduleId[
      course.agenda_atividade_detalhe_pai_id ||
        course.agenda_atividade_detalhe_id
    ];
  const classes = useStyles();
  const translation = useTranslation();
  const [openForms, setOpenForms] = useState({});
  const [openQuestions, setOpenQuestions] = useState({});

  const toggleForm = (formIndex, shouldOpen) => {
    setOpenForms(() => {
      return shouldOpen ? { [formIndex]: true } : {};
    });
    setOpenQuestions({});
  };

  const toggleQuestion = (formIndex, questionIndex) => {
    setOpenQuestions((previousQuestions) => {
      const isOpen = previousQuestions[formIndex]?.includes(questionIndex);

      return isOpen
        ? {}
        : {
            [formIndex]: [questionIndex],
          };
    });
  };

  const getAnswerLetter = (question, isCorrectAnswer = false) => {
    if (!question?.resposta) return "-";

    if (isCorrectAnswer) {
      const correctAnswerIndex = question?.resposta.findIndex(
        (response) => response.resposta_correta === "S"
      );
      return correctAnswerIndex === -1
        ? "-"
        : String.fromCharCode(65 + correctAnswerIndex);
    } else {
      const userAnswer = question?.pessoa_resposta?.[0]?.descricao || "";
      const userAnswerIndex = question?.resposta.findIndex(
        (response) => response.descricao === userAnswer
      );
      return userAnswerIndex === -1
        ? "-"
        : String.fromCharCode(65 + userAnswerIndex);
    }
  };

  const renderAnswers = (question, formResults) => {
    const selectedAnswerIndex = question?.resposta.findIndex(
      (response) =>
        response.descricao === question?.pessoa_resposta?.[0]?.descricao
    );
    const correctAnswerIndex = question?.resposta.findIndex(
      (response) => response.resposta_correta === "S"
    );

    return (
      <Box className={classes.answersBox}>
        {question?.resposta.map((answer, index) => {
          let className = classes.answerText;
          if (
            index === correctAnswerIndex &&
            formResults?.ead_exibir_gabarito === "S"
          ) {
            className = classes.correctAnswer;
          } else if (
            index === selectedAnswerIndex &&
            index !== correctAnswerIndex &&
            formResults?.ead_exibir_gabarito === "S"
          ) {
            className = classes.incorrectAnswer;
          } else if (
            index === selectedAnswerIndex &&
            formResults?.ead_exibir_gabarito === "N"
          ) {
            className = classes.selectedAnswerText;
          }

          let label = null;
          if (
            index === correctAnswerIndex &&
            index !== selectedAnswerIndex &&
            formResults?.ead_exibir_gabarito === "S"
          ) {
            label = translation.correctAnswer;
          } else if (
            index === selectedAnswerIndex &&
            (((index === correctAnswerIndex || index !== correctAnswerIndex) &&
              formResults?.ead_exibir_gabarito === "S") ||
              formResults?.ead_exibir_gabarito === "N")
          ) {
            label = translation.yourAnswer;
          }

          return (
            <Typography key={index} className={className}>
              {String.fromCharCode(65 + index)}) {answer.descricao}
              {label && <span className={classes.answerStatus}> {label}</span>}
            </Typography>
          );
        })}
      </Box>
    );
  };

  const renderQuestion = (question, questionIndex, formIndex, formResults) => {
    const isOpen =
      !!openQuestions[formIndex]?.includes(questionIndex) &&
      formResults?.ead_exibir_respostas === "S";
    const userAnswerLetter = getAnswerLetter(question, false);
    const correctAnswerLetter = getAnswerLetter(question, true);
    const isCorrect =
      userAnswerLetter === correctAnswerLetter && userAnswerLetter !== "-";

    return (
      <Accordion
        expanded={isOpen}
        onChange={() => toggleQuestion(formIndex, questionIndex)}
        disabled={formResults?.ead_exibir_respostas !== "S"}
        key={questionIndex}
        className={classes.questionBox}
      >
        <AccordionSummary className={classes.questionHeader}>
          <Box className={classes.accordionArrowWrapper}>
            <ExpandMore
              className={`${classes.accordionArrow} ${
                isOpen ? classes.expanded : ""
              }`}
            />
          </Box>
          <Typography className={classes.questionTitle}>
            {questionIndex + 1} - {question?.descricao}
          </Typography>
          <Box className={classes.questionHeaderRight}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Icon>analytics</Icon>}
            >
              {isOpen ? translation.hideAnswer : translation.showAnswer}
            </Button>
            <Typography className={classes.resultStats}>
              {formResults?.ead_exibir_gabarito === "S" && (
                <>
                  {translation.result}:{" "}
                  <span
                    className={
                      isCorrect
                        ? classes.correctResult
                        : classes.incorrectResult
                    }
                  >
                    {isCorrect
                      ? translation.correctResult
                      : translation.incorrectResult}
                  </span>{" "}
                  -{" "}
                </>
              )}
              {translation.yourAnswer}:{" "}
              <span className={classes.userAnswerLetter}>
                {userAnswerLetter}
              </span>
              {formResults?.ead_exibir_gabarito === "S" && (
                <>
                  {" "}
                  - {translation.correctAnswer}:{" "}
                  <span className={classes.correctAnswerLetter}>
                    {correctAnswerLetter}
                  </span>
                </>
              )}
            </Typography>
          </Box>
        </AccordionSummary>

        <AccordionDetails className={classes.questionContent}>
          {renderAnswers(question, formResults)}
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderForm = (form, formIndex) => {
    const formResults = finalResults.find(
      (result) => result.descricao_formulario === form?.descricao
    );
    const isFormOpen = !!openForms[formIndex];
    const formTitle = `${translation.questionnaire} ${
      formIndex === -1 ? "final" : formIndex + 1
    } - ${form?.descricao}`;
    const resultAnsweredDate =
      form?.pergunta?.[0]?.pessoa_resposta?.[0]?.data_resposta;

    return (
      <Accordion
        expanded={isFormOpen}
        disabled={
          formResults?.ead_exibir_respostas !== "S" || !form?.respondido
        }
        onChange={() => toggleForm(formIndex, !isFormOpen)}
        key={formIndex}
        className={classes.formContainer}
      >
        <AccordionSummary className={classes.formHeader}>
          <Box className={classes.accordionArrowWrapper}>
            <ExpandMore
              className={`${classes.accordionArrow} ${
                isFormOpen ? classes.expanded : ""
              }`}
            />
          </Box>
          <Typography className={classes.formTitle}>{formTitle}</Typography>
          <Box className={classes.formHeaderRight}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Icon>analytics</Icon>}
              onClick={() => toggleForm(formIndex, !isFormOpen)}
            >
              {isFormOpen ? translation.hideAnswers : translation.showAnswers}
            </Button>
            {form?.respondido && (
              <Typography className={classes.formStatus}>
                {formResults?.ead_exibir_desempenho === "S" && (
                  <>
                    {translation.performance}:{" "}
                    <span className={classes.performancePercentage}>
                      {form?.porcentagem_respostas_corretas}%
                    </span>{" "}
                    -{" "}
                  </>
                )}
                {translation.answeredOn}:{" "}
                <span className={classes.answerDate}>
                  {new Date(`${resultAnsweredDate}T00:00:00`).toLocaleDateString("pt-BR")}
                </span>
              </Typography>
            )}
            {!form?.respondido && (
              <Typography className={classes.formStatus}>
                {translation.questionnaireNotAnswered}:
                <span className={classes.notAnsweredQuiz}>
                  {formResults?.tipo === "FV"
                    ? translation.notAnsweredQuizFV
                    : translation.notAnsweredQuizFA}
                </span>
              </Typography>
            )}
          </Box>
        </AccordionSummary>

        <AccordionDetails className={classes.formQuestions}>
          {form?.pergunta.map((question, questionIndex) =>
            renderQuestion(question, questionIndex, formIndex, formResults)
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  const classesModal = useStyles();

  const mainData = results?.data?.[0];

  return (
    <ModalFullScreen>
      <Container className={classesModal.modalContent}>
        <Typography className={classesModal.textHeader}>
          {translation.results}
        </Typography>
        <IconButton onClick={closeShow} className={classesModal.iconHeader}>
          <Icon>highlight_off</Icon>
        </IconButton>

        <Box className={classesModal.courseInfoContainer}>
          <Typography className={classesModal.courseInfo}>
            <School fontSize="small" /> {course?.descricao}
          </Typography>
          <Typography className={classesModal.courseInfo}>
            <EventNote fontSize="small" /> {course?.titulo_palestra}
          </Typography>
          <Typography className={classesModal.courseInfo}>
            <People fontSize="small" /> {course?.palestrantes_nome}
          </Typography>
          <Box className={classesModal.profileContainer}>
            <Avatar src={user?.data?.foto} alt={user?.data?.nome} />
            <Typography>{user?.data?.nome}</Typography>
          </Box>
          <Divider className={classesModal.divider} />
        </Box>

        {isLoading && (
          <Box className={classesModal.loadingContainer}>
            <CircularProgress />
          </Box>
        )}

        {!isLoading && !mainData && (
          <Box className={classesModal.noResultsContainer}>
            <Typography className={classesModal.noResultsText}>
              {translation.noResults}
            </Typography>
          </Box>
        )}

        {!isLoading &&
          mainData?.formulario &&
          renderForm(mainData.formulario, -1)}

        {!isLoading &&
          mainData?.formularios &&
          mainData.formularios.map((form, formIndex) =>
            renderForm(form, formIndex)
          )}
      </Container>
    </ModalFullScreen>
  );
}
